import React from 'react'
import { match } from 'ts-pattern'

import { CustomerAttributeMultiSelector } from '~/components/features/CustomerAttributeMultiSelector'
import {
  CustomerAttributeSelector,
  type CustomerAttributeSelectorProps,
} from '~/components/features/CustomerAttributeSelector'
import {
  type ConditionOperation,
  type CustomerAttribute as CustomerAttributeBase,
} from '~/gql/graphql'

type CustomerAttribute = App.PickGraphQL<CustomerAttributeBase, 'id' | 'name'>

export type CustomerAttributeConditionalSelectorProps<
  TCustomerAttribute extends CustomerAttribute,
> = {
  className?: string
  conditionOperation: ConditionOperation
  customerAttributes: TCustomerAttribute[]
  disabled?: boolean
  onBlur?: () => void
  onChange?: (customerAttributeIds: TCustomerAttribute['id'][]) => void
  value: TCustomerAttribute['id'][]
}

export const CustomerAttributeConditionalSelector = React.forwardRef(
  <TCustomerAttribute extends CustomerAttribute>(
    {
      className,
      conditionOperation,
      customerAttributes,
      disabled,
      onBlur,
      onChange,
      value,
    }: CustomerAttributeConditionalSelectorProps<TCustomerAttribute>,
    ref: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const handleCustomerAttributeChange = React.useCallback<
      EH<CustomerAttributeSelectorProps<TCustomerAttribute>, 'onChange'>
    >(
      (customerAttributeId) => {
        onChange?.([customerAttributeId])
      },
      [onChange],
    )

    return (
      <>
        {match(conditionOperation)
          .with('equal', 'not_equal', () => (
            <CustomerAttributeSelector
              ref={ref}
              className={className}
              customerAttributes={customerAttributes}
              disabled={disabled}
              value={value[0]}
              onBlur={onBlur}
              onChange={handleCustomerAttributeChange}
            />
          ))
          .with('in', 'not_in', () => (
            <CustomerAttributeMultiSelector
              ref={ref}
              className={className}
              customerAttributes={customerAttributes}
              disabled={disabled}
              value={value}
              onBlur={onBlur}
              onChange={onChange}
            />
          ))
          .exhaustive()}
      </>
    )
  },
)
